import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Accordion, AccordionSummary, AccordionDetails, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useParams } from 'react-router';
import { apiGetStatsByLottery, type LotteryStats, type StatEntry } from 'API/lotteryApi';
import { PageHeader } from 'components/Layout/PageHeader';

const MONTH_NAMES = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const HitsTable: React.FC<{ overviewHits: StatEntry[]; queryHits: StatEntry[] }> = ({ overviewHits, queryHits }) => {
  const allKeys = new Set([
    ...overviewHits.map((e) => `${e.year}-${e.month}`),
    ...queryHits.map((e) => `${e.year}-${e.month}`),
  ]);
  const rows = Array.from(allKeys).sort().map((key) => {
    const [year, month] = key.split('-').map(Number);
    const ov = overviewHits.find((e) => e.year === year && e.month === month);
    const qr = queryHits.find((e) => e.year === year && e.month === month);
    return { year, month, overview: ov?.count ?? 0, query: qr?.count ?? 0 };
  });

  if (rows.length === 0) {
    return <Typography variant="body2" sx={{ color: '#969696' }}>Keine Daten vorhanden.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Monat</TableCell>
          <TableCell align="right">Aufrufe (Übersicht)</TableCell>
          <TableCell align="right">Aufrufe (Abfrage)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.year}-${row.month}`}>
            <TableCell>{MONTH_NAMES[row.month - 1]} {row.year}</TableCell>
            <TableCell align="right">{row.overview}</TableCell>
            <TableCell align="right">{row.query}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const StatisticsPage: React.FC = () => {
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const [stats, setStats] = useState<LotteryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lotteryId) return;
    let cancelled = false;
    apiGetStatsByLottery(Number(lotteryId))
      .then((data) => { if (!cancelled) { setStats(data); setError(null); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Die Statistik konnte nicht geladen werden.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [lotteryId]);

  return (
    <Box>
      <PageHeader title="Statistik" />

      {loading && <CircularProgress />}

      {error && (
        <Typography variant="body1" sx={{ color: '#969696' }}>
          {error}
        </Typography>
      )}

      {!loading && stats && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Gesamt</Typography>
            <HitsTable overviewHits={stats.overviewHits} queryHits={stats.queryHits} />
          </Paper>

          {stats.byBranch.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ marginBottom: 1 }}>Nach Filiale</Typography>
              {stats.byBranch.map((branch) => (
                <Accordion key={branch.branchCode}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{branch.branchName} ({branch.branchCode})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <HitsTable overviewHits={branch.overviewHits} queryHits={branch.queryHits} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
